import { buildCloudinaryImageSources } from '../../util/cloudinary';

/**
 * Single source of truth for the listing gallery's Cloudinary image contract.
 *
 * Three places have to agree on exactly one URL for the first gallery slide: SectionGallery
 * (which builds the Cloudinary lookup), ListingImageGallery (which renders the slide) and
 * ListingPageCarousel (which preloads it). When those were computed independently they drifted —
 * the preload requested `ras_hero` (w_1400, q_auto:good) while the gallery rendered `ras_product`
 * (w_800, q_auto:eco), so the page paid for a high-priority full-size download it never showed
 * and the actual LCP image did not start until React had hydrated.
 *
 * GALLERY_IMAGE_OPTIONS mirrors what <ResponsiveImage> ends up with after merging its own
 * Cloudinary defaults over what the gallery passes down, so URLs built here are byte-identical
 * to the ones it renders.
 */
export const GALLERY_IMAGE_VARIANTS = ['ras_product'];
export const GALLERY_IMAGE_DEFAULT_VARIANT = 'ras_product';
export const GALLERY_IMAGE_OPTIONS = {
  format: 'auto',
  quality: 'auto:eco',
  dpr: 'auto',
  crop: 'fit',
};

/**
 * Map Sharetribe image UUID -> Cloudinary secure_url.
 *
 * Primary key:  sharetribeImgId (always the Sharetribe UUID when present)
 * Fallback key: public_id       (for user-uploaded images public_id === the Sharetribe UUID,
 *                                because the UUID is used as the Cloudinary public_id at upload
 *                                time — see ListingWizardPage.duck.js)
 *
 * An image with neither key matching falls through to the Sharetribe CDN variants.
 */
export const buildCloudinaryUrlMap = listingImages =>
  Array.isArray(listingImages)
    ? listingImages.reduce((acc, img) => {
        if (!img?.secure_url) return acc;
        if (img.sharetribeImgId) {
          acc[img.sharetribeImgId] = img.secure_url;
        } else if (img.public_id) {
          acc[img.public_id] = img.secure_url;
        }
        return acc;
      }, {})
    : {};

/**
 * Build the `<link rel="preload">` descriptor for the gallery's first slide, or null when there
 * is nothing safe to preload.
 *
 * Keyed off `listing.images[0]` rather than `publicData.listingImages[0]` because the gallery
 * slides are driven by the Sharetribe image order — the two arrays are not guaranteed to agree,
 * and preloading the wrong one is worse than preloading nothing.
 *
 * Returns the WebP candidate with `type="image/webp"`, matching the <source> that
 * <ResponsiveImage> emits. Browsers that cannot decode WebP skip both the preload and that
 * <source>, and fetch the JPEG <img> fallback normally.
 */
export const getGalleryHeroPreload = listing => {
  const firstImageUuid = listing?.images?.[0]?.id?.uuid;
  if (!firstImageUuid) return null;

  const secureUrl = buildCloudinaryUrlMap(listing?.attributes?.publicData?.listingImages)[
    firstImageUuid
  ];
  // No Cloudinary original for this slide: the gallery renders Sharetribe CDN variants instead,
  // which this helper does not describe. Preload nothing rather than guess at a URL.
  if (!secureUrl) return null;

  const { srcset, sizes, defaultUrl } = buildCloudinaryImageSources({
    secureUrl,
    variantsToRender: GALLERY_IMAGE_VARIANTS,
    defaultVariant: GALLERY_IMAGE_DEFAULT_VARIANT,
    options: GALLERY_IMAGE_OPTIONS,
  });

  return defaultUrl
    ? { href: defaultUrl, imagesrcset: srcset, imagesizes: sizes, type: 'image/webp' }
    : null;
};
